// src/components/Dashboard/LiveRidesDashboard.js
import { Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Import components
import truckDriverIcon from '../../assets/truck_driver.png';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import convoyDetailsCache from '../../services/convoyDetailsCache';
import dashboardCache from '../../services/liveRideDashboardCache.js';
import { driverApi } from '../Driver/driverApi.js';
import leaveApi from '../LeaveManagement/leaveApi.js';
import { getVehicleById } from '../Onboarding/vehicleOnboardingApi.js';
import { getSiteDetails } from '../SiteManagement/siteApi.js';
import MapComponent from './MapComponent.jsx';
import TrackRidesComponent from './TrackRidesComponent.jsx';
import { dashboardApi } from './dashboardApi.js';

// Utility function to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism styles
const glassmorphism = 'bg-white/20 backdrop-blur-md rounded-xl border border-white/10 shadow-lg';

const LiveRidesDashboard = ({ isAuthenticated }) => {
  // Log screen view
  useScreenLogging('LiveRidesDashboard', {
    component: 'LiveRidesDashboard',
    isAuthenticated: !!isAuthenticated,
  });
  // State for data
  const [liveRides, setLiveRides] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRideForMap, setSelectedRideForMap] = useState(null);
  
  // State for map
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 }); // Default center for India
  const [mapZoom, setMapZoom] = useState(5);
  
  // Store all office locations (unfiltered)
  const [allOfficeLocations, setAllOfficeLocations] = useState([]);
  
  // State for driver tracking
  const [selectedDriver, setSelectedDriver] = useState(null); // {rideId, userId, driverName}
  
  // State for ride info table
  const [showRideInfoTable, setShowRideInfoTable] = useState(false);
  const [rideTableData, setRideTableData] = useState([]);
  const [loadingTableData, setLoadingTableData] = useState(false);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for mobile viewport detection
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  
  const mapComponentRef = useRef();
  
  // Cache for driver details to avoid repeated API calls
  const driverDetailsCacheRef = useRef(new Map()); // Map<userId, { name, timestamp }>

  // Fetch and display driver location (with path) for selected driver
  const fetchAndDisplayDriverLocation = useCallback(async () => {
    if (!selectedDriver || !mapComponentRef.current) return;

    try {
      // Check driver status first - don't fetch location if status is "Duty not started"
      // Check cache first
      let driverStatusData = dashboardCache.getDriverStatus(
        selectedDriver.rideId,
        selectedDriver.userId
      );
      if (!driverStatusData) {
        driverStatusData = await driverApi.getDriverStatus(
          selectedDriver.rideId,
          selectedDriver.userId
        );
        dashboardCache.setDriverStatus(selectedDriver.rideId, selectedDriver.userId, driverStatusData);
      }
      
      const driverStatus = driverStatusData?.status?.toString() || '';
      
      if (driverStatus.toLowerCase() === 'duty not started') {
        console.log('📍 Debug: Skipping location fetch for driver with status "Duty not started"');
        // Clear any existing driver tracking
        if (mapComponentRef.current.clearDriverTracking) {
          mapComponentRef.current.clearDriverTracking();
        }
        if (mapComponentRef.current.clearConvoyDriverMarkers) {
          mapComponentRef.current.clearConvoyDriverMarkers();
        }
        return;
      }

      // Clear convoy markers when showing specific driver path
      if (mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }

      const locationData = await driverApi.fetchDriverLocation(
        selectedDriver.rideId,
        selectedDriver.userId
      );

      console.log('📍 Debug: Driver location data received:', locationData);

      if (locationData && locationData.locations && Array.isArray(locationData.locations)) {
        const locations = locationData.locations;
        
        if (locations.length > 0 && mapComponentRef.current.displayDriverPath) {
          // Pass driver name to display in the popup instead of user ID
          const driverName = selectedDriver.driverName || 'Driver Location';
          mapComponentRef.current.displayDriverPath(locations, truckDriverIcon, driverName);
          console.log(`✅ Debug: Displayed ${locations.length} driver location points`);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching driver location:', error);
    }
  }, [selectedDriver]);

  // Fetch and display driver markers (no path) for all convoy members when ride is selected
  const fetchAndDisplayConvoyDriverMarkers = useCallback(async () => {
    if (!selectedRideForMap || !mapComponentRef.current) {
      // Clear markers when no ride is selected
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
      return;
    }

    // Don't show convoy markers if a specific driver is selected (they want to see the path)
    if (selectedDriver) {
      return;
    }

    try {
      const selectedRide = liveRides.find(ride => ride.id === selectedRideForMap);
      if (!selectedRide || !selectedRide.convoy_details || !Array.isArray(selectedRide.convoy_details)) {
        return;
      }

      // Fetch current location for each driver in convoy
      const driverLocationPromises = selectedRide.convoy_details.map(async (convoyMember) => {
        try {
          // Check driver status first - don't fetch location if status is "Duty not started"
          const rideId = selectedRide.ride_id || selectedRide.id;
          // Check cache first
          let driverStatusData = dashboardCache.getDriverStatus(rideId, convoyMember.user_id);
          if (!driverStatusData) {
            driverStatusData = await driverApi.getDriverStatus(rideId, convoyMember.user_id);
            dashboardCache.setDriverStatus(rideId, convoyMember.user_id, driverStatusData);
          }
          
          const driverStatus = driverStatusData?.status?.toString() || '';
          
          if (driverStatus.toLowerCase() === 'duty not started') {
            console.log(`📍 Debug: Skipping location fetch for driver ${convoyMember.user_id} with status "Duty not started"`);
            return null;
          }

          const locationData = await driverApi.fetchDriverLocation(
            selectedRide.ride_id || selectedRide.id,
            convoyMember.user_id
          );

          // Get the latest location from the locations array
          if (locationData && locationData.locations && Array.isArray(locationData.locations) && locationData.locations.length > 0) {
            const latestLocation = locationData.locations[locationData.locations.length - 1];
            
            // Get driver name: try convoy member data first, then look up from employees list, then API, fallback to "Driver"
            // Never use user_id as the display name
            let driverName = convoyMember.driver?.name || convoyMember.name;
            
            // If name not found in convoy member, look it up from employees list
            if (!driverName && convoyMember.user_id && employees.length > 0) {
              const employee = employees.find(emp => emp.id === convoyMember.user_id);
              driverName = employee?.name;
            }
            
            // If still not found, fetch from API using driverApi.fetchDriverDetails
            if (!driverName && convoyMember.user_id) {
              try {
                // Check cache first
                const cachedDriver = driverDetailsCacheRef.current.get(convoyMember.user_id);
                if (cachedDriver && (Date.now() - cachedDriver.timestamp < 5 * 60 * 1000)) {
                  // Cache valid for 5 minutes
                  driverName = cachedDriver.name;
                } else {
                  // Fetch from API
                  const driverDetails = await driverApi.fetchDriverDetails(convoyMember.user_id);
                  // Try different possible field names for the user name
                  driverName = driverDetails?.user_name || 
                             null;
                  
                  // Cache the result if we got a name
                  if (driverName) {
                    driverDetailsCacheRef.current.set(convoyMember.user_id, {
                      name: driverName,
                      timestamp: Date.now()
                    });
                  }
                }
              } catch (error) {
                console.warn(`Failed to fetch driver details for ${convoyMember.user_id}:`, error);
                // Continue with fallback
              }
            }
            
            // Final fallback to "Driver" (never use user_id)
            driverName = driverName || 'Driver';
            
            return {
              latitude: latestLocation.latitude,
              longitude: latestLocation.longitude,
              timestamp: latestLocation.timestamp,
              driverName: driverName,
            };
          }
          return null;
        } catch (error) {
          console.warn(`Failed to fetch location for driver ${convoyMember.user_id}:`, error);
          return null;
        }
      });

      const driverLocations = (await Promise.all(driverLocationPromises)).filter(loc => loc !== null);

      if (driverLocations.length > 0 && mapComponentRef.current.displayDriverMarkersOnly) {
        mapComponentRef.current.displayDriverMarkersOnly(driverLocations, truckDriverIcon);
        console.log(`✅ Debug: Displayed ${driverLocations.length} convoy driver markers`);
      }
    } catch (error) {
      console.error('❌ Error fetching convoy driver locations:', error);
    }
  }, [selectedRideForMap, liveRides, selectedDriver, employees]);

  // Display driver location (with path) when driver is selected (no auto-refresh)
  useEffect(() => {
    if (selectedDriver && mapComponentRef.current && selectedRideForMap) {
      // Fetch and display driver location once when selected (no periodic updates)
      fetchAndDisplayDriverLocation();

      // Cleanup: clear tracking when driver is deselected or component unmounts
      return () => {
        if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
          mapComponentRef.current.clearDriverTracking();
        }
      };
    } else {
      // Clear driver tracking when no driver is selected OR when ride is deselected
      if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
        mapComponentRef.current.clearDriverTracking();
      }
      // When driver is deselected but ride is still selected, show convoy markers again
      if (selectedRideForMap && !selectedDriver) {
        fetchAndDisplayConvoyDriverMarkers();
      }
    }
  }, [selectedDriver, fetchAndDisplayDriverLocation, selectedRideForMap, fetchAndDisplayConvoyDriverMarkers]);

  // Fetch and display convoy driver markers when ride is selected (no auto-refresh)
  useEffect(() => {
    if (selectedRideForMap && !selectedDriver) {
      // Fetch convoy driver markers when a ride is selected (no periodic updates)
      fetchAndDisplayConvoyDriverMarkers();

      return () => {
        // Clear convoy markers when ride is deselected
        if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
          mapComponentRef.current.clearConvoyDriverMarkers();
        }
      };
    } else if (!selectedRideForMap) {
      // Clear convoy markers when no ride is selected
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
    }
  }, [selectedRideForMap, selectedDriver, fetchAndDisplayConvoyDriverMarkers]);

  // Fetch live rides based on API logic
  const fetchLiveRides = useCallback(async (forceRefresh = false) => {
    try {
      const now = new Date();
      
      // Helper function to get site name with caching
      const getSiteName = async (siteId) => {
        if (!siteId || siteId.trim() === '') {
          return 'Unknown';
        }
        
        // Check convoy details cache first (shared with RideDetails)
        const cachedName = convoyDetailsCache.getSite(siteId);
        if (cachedName) {
          return cachedName;
        }
        
        try {
          const siteData = await getSiteDetails(siteId);
          const siteName = siteData?.site_name || 'Unknown';
          convoyDetailsCache.setSite(siteId, siteName);
          return siteName;
        } catch (error) {
          console.error(`Error fetching site details for ${siteId}:`, error);
          return 'Unknown';
        }
      };

      // Get today's date in YYYY-MM-DD format
      const todayDate = now.toISOString().split('T')[0];
      console.log("🔍 Debug: Fetching live rides for date:", todayDate);

      // Check cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedRides = dashboardCache.getLiveRides(todayDate);
        if (cachedRides) {
          console.log('📦 Using cached live rides');
          setLiveRides(cachedRides);
          return;
        }
      } else {
        // Clear cache on force refresh
        dashboardCache.clearLiveRides();
      }

      // Fetch all driver schedules for today
      const allRides = await driverApi.fetchDriverSchedulesByDate(todayDate);
      console.log("🔍 Debug: API returned", allRides.length, "total rides for today");

      const liveRidesList = [];

      // Process each ride to check if it's currently ongoing
      for (const ride of allRides) {
        const fromSiteId = ride.from_site_id || '';
        const toSiteId = ride.to_site_id || '';

        // Get timestamps in ISO format
        const startTimestampStr = ride.ride_start_timestamp;
        const endTimestampStr = ride.ride_end_timestamp;

        console.log(
          "🔍 Debug: Processing ride - Start:", startTimestampStr, 
          "End:", endTimestampStr
        );

        if (!startTimestampStr || !endTimestampStr) {
          console.log("⚠️ Debug: Skipping ride due to missing timestamps");
          continue;
        }

        try {
          // Parse ISO timestamps to Date objects
          const startTime = new Date(startTimestampStr);
          const endTime = new Date(endTimestampStr);

          // Ensure all times are in UTC for accurate comparison
          const nowUtc = new Date(now.toISOString());
          const startTimeUtc = new Date(startTime.toISOString());
          const endTimeUtc = new Date(endTime.toISOString());

          // Add a small buffer (2 minutes) to account for potential clock differences
          const nowWithBuffer = new Date(nowUtc.getTime() + 2 * 60 * 1000); // +2 minutes
          const startWithBuffer = new Date(startTimeUtc.getTime() - 2 * 60 * 1000); // -2 minutes

          console.log(
            "🔍 Debug: Parsed times - Start:", startTimeUtc, 
            "End:", endTimeUtc, 
            "Now:", nowUtc
          );
          console.log(
            "🔍 Debug: With buffer - Start:", startWithBuffer, 
            "Now:", nowWithBuffer
          );

          // Check if the ride is ongoing (with buffer for clock differences)
          const isAfterStart = nowWithBuffer.getTime() > startWithBuffer.getTime();
          const isBeforeEnd = nowUtc.getTime() < endTimeUtc.getTime();

          console.log(
            "🔍 Debug: Time comparison - isAfter start:", isAfterStart, 
            "isBefore end:", isBeforeEnd
          );

          if (isAfterStart && isBeforeEnd) {
            console.log("✅ Debug: Found ongoing ride!");

            // Fetch site names and supervisor names in parallel for better performance
            const fetchPromises = [
              getSiteName(fromSiteId),
              getSiteName(toSiteId),
            ];

            // Add supervisor fetching promises if IDs are available
            if (ride.from_supervisor_id) {
              fetchPromises.push(
                leaveApi.getUserInfo(ride.from_supervisor_id).catch(error => {
                  console.error('Error fetching from supervisor details:', error);
                  return null;
                })
              );
            } else {
              fetchPromises.push(Promise.resolve(null));
            }

            if (ride.to_supervisor_id) {
              fetchPromises.push(
                leaveApi.getUserInfo(ride.to_supervisor_id).catch(error => {
                  console.error('Error fetching to supervisor details:', error);
                  return null;
                })
              );
            } else {
              fetchPromises.push(Promise.resolve(null));
            }

            const [fromSiteName, toSiteName, fromSupervisorData, toSupervisorData] = await Promise.all(fetchPromises);

            // Extract supervisor names from fetched data
            const fromSupervisorName = fromSupervisorData?.user_name || fromSupervisorData?.name || null;
            const toSupervisorName = toSupervisorData?.user_name || toSupervisorData?.name || null;

            // Process halts if available
            let halts = [];
            if (ride.halts && Array.isArray(ride.halts)) {
              halts = ride.halts.map((halt) => {
                if (typeof halt === 'string') {
                  // Legacy format - just address
                  return {
                    address: halt,
                    coordinates: null,
                  };
                } else if (halt && typeof halt === 'object') {
                  // New format - with coordinates
                  return {
                    address: halt.address || halt.toString(),
                    coordinates: halt.coordinates || null,
                  };
                }
                return {
                  address: halt.toString(),
                  coordinates: null,
                };
              });
            }

            // Create ride object matching the expected format
            liveRidesList.push({
              id: ride.ride_id || `ride_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
              ride_id: ride.ride_id, // Keep original ride_id for API calls
              status: 'in progress',
              from_site_id: fromSiteId,
              to_site_id: toSiteId,
              startSiteName: fromSiteName,
              endSiteName: toSiteName,
              fromSupervisorName: fromSupervisorName, // Include supervisor names
              toSupervisorName: toSupervisorName,
              halts: halts, // Include halts data
              convoy_details: ride.convoy_details || [], // Include convoy details for driver tracking
              // Include other fields if available
              vehicle: ride.vehicle || 'N/A',
              driver: ride.driver || 'N/A',
              material: ride.material || 'N/A',
              speed: ride.speed || null,
              battery: ride.battery || null,
              ride_start_timestamp: startTimestampStr,
              ride_end_timestamp: endTimestampStr,
            });
          } else {
            console.log("⏰ Debug: Ride is not currently ongoing");
          }
        } catch (parseError) {
          console.error("❌ Debug: Error parsing timestamps:", parseError);
          continue;
        }
      }

      console.log("🔍 Debug: Final result -", liveRidesList.length, "ongoing rides found");
      console.log("🔍 Debug: Ongoing Rides:", liveRidesList);

      // Cache the live rides list
      dashboardCache.setLiveRides(todayDate, liveRidesList);

      setLiveRides(liveRidesList);
    } catch (error) {
      console.error("❌ Debug: Error fetching live rides:", error);
      setError(error.message || 'Failed to load live rides');
      // Don't clear existing rides on error, just log it
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated) {
        console.log('LiveRidesDashboard: User not authenticated, skipping API calls');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Check cache first for sites and users
        let allSitesResponse = dashboardCache.getAllSites();
        let allUsersResponse = dashboardCache.getAllUsers();
        
        // Fetch if not cached
        if (!allSitesResponse || !allUsersResponse) {
          const [fetchedSites, fetchedUsers] = await Promise.all([
            allSitesResponse ? Promise.resolve(allSitesResponse) : dashboardApi.getAllSites(),
            allUsersResponse ? Promise.resolve(allUsersResponse) : dashboardApi.getAllUsersWithDutyData(),
          ]);
          
          if (!allSitesResponse) {
            allSitesResponse = fetchedSites;
            dashboardCache.setAllSites(fetchedSites);
          }
          
          if (!allUsersResponse) {
            allUsersResponse = fetchedUsers;
            dashboardCache.setAllUsers(fetchedUsers);
          }
        }

        // Process sites data
        const sitesArray = Array.isArray(allSitesResponse) ? allSitesResponse : [];
        const fetchedOfficeLocations = sitesArray.map(site => {
          let lat = 0;
          let lng = 0;
          let geofenceDetails = null;

          try {
            const parsedDetails = JSON.parse(site.geofence_details);
            geofenceDetails = parsedDetails;

            if (parsedDetails.type === 'Polygon' && parsedDetails.coordinates && parsedDetails.coordinates.length > 0) {
              lat = parsedDetails.coordinates[0].latitude;
              lng = parsedDetails.coordinates[0].longitude;
            } else if (parsedDetails.type === 'Circle' && parsedDetails.center) {
              lat = parsedDetails.center.latitude;
              lng = parsedDetails.center.longitude;
            } else {
              lat = 20.5937;
              lng = 78.9629;
            }
          } catch (parseError) {
            console.error(`Error parsing geofence_details for site ${site.site_id}:`, parseError);
            lat = 20.5937;
            lng = 78.9629;
          }

          return {
            id: site.site_id,
            name: site.site_name,
            address: site.address,
            lat: lat,
            lng: lng,
            geofenceDetails: geofenceDetails,
            radius: geofenceDetails?.radius || 500,
          };
        });
        
        // Store all office locations (unfiltered)
        setAllOfficeLocations(fetchedOfficeLocations);
        // officeLocations will be set by filteredOfficeLocations useEffect once liveRides are loaded

        // Process employees data
        const allUsersArray = allUsersResponse && allUsersResponse.users ? allUsersResponse.users : [];
        const fetchedEmployees = await Promise.all(
          allUsersArray.map(async user => {
            let imageUrl = `https://placehold.co/100x100/aabbcc/ffffff?text=${user.user_name ? user.user_name.charAt(0) : '?'}`;
            if (user.image_path) {
              try {
                imageUrl = await dashboardApi.getPresignedS3Url(user.image_path);
                if (!imageUrl) {
                  imageUrl = `https://placehold.co/100x100/ff0000/ffffff?text=Error`;
                }
              } catch (imgError) {
                console.error(`Failed to get presigned URL for ${user.user_name}:`, imgError);
                imageUrl = `https://placehold.co/100x100/ff0000/ffffff?text=Error`;
              }
            }
            return {
              id: user.user_id,
              name: user.user_name,
              role: user.role_name,
              image: imageUrl,
              officeId: user.site_id || 'N/A',
              status: user.on_duty ? 'On Duty' : 'Off Duty',
              note: user.spec_role || 'N/A',
              lat: user.last_location?.latitude,
              lng: user.last_location?.longitude,
            };
          })
        );
        
        setEmployees(fetchedEmployees);
        
        // Fetch live rides after sites and employees are loaded
        await fetchLiveRides();
        
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError(err.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchInitialData();
    }
  }, [isAuthenticated, fetchLiveRides]);

  // Callback for getting selected ride route
  const getSelectedRideRoute = useCallback(() => {
    if (!selectedRideForMap) return null;
    return liveRides.find(ride => ride.id === selectedRideForMap);
  }, [selectedRideForMap, liveRides]);

  // Callback for showing ride info table
  const handleShowRideInfoTable = useCallback(async (rideList) => {
    try {
      // Check if it's convoy data (has serialNumber, vehicleId, userId)
      const isConvoyData = Array.isArray(rideList) && rideList.length > 0 && 
                           (rideList[0].serialNumber !== undefined || rideList[0].vehicleId !== undefined);
      
      if (!isConvoyData || !Array.isArray(rideList) || rideList.length === 0) {
        // If no convoy data, use existing logic
        const dataToShow = Array.isArray(rideList) && rideList.length > 0 
          ? rideList 
          : liveRides;
        setRideTableData(Array.isArray(dataToShow) ? dataToShow : []);
        setShowRideInfoTable(true);
        return;
      }

      // Show loading state
      setLoadingTableData(true);
      setShowRideInfoTable(true);

      // Fetch detailed information for each convoy member
      const enrichedData = await Promise.all(
        rideList.map(async (convoyMember, index) => {
          const serialNumber = convoyMember.serialNumber || index + 1;
          const vehicleId = convoyMember.vehicleId || convoyMember.vehicle_id || 'N/A';
          const userId = convoyMember.userId || convoyMember.user_id || 'N/A';
          const rideId = convoyMember.rideId || convoyMember.ride_id || 'N/A';
          const fromSiteId = convoyMember.fromSiteId || convoyMember.from_site_id;
          const toSiteId = convoyMember.toSiteId || convoyMember.to_site_id;

          // Initialize default values
          let vehicleName = "Unknown Vehicle";
          let driverName = "Unknown Driver";
          let parkingDestination = "Unknown Station";
          // Material comes from convoy member, not the ride
          let material = convoyMember.material || "N/A";
          let speed = "N/A";
          let battery = "N/A";
          let statusMessage = "Unknown Status";

          // Fetch vehicle details
          if (vehicleId && vehicleId !== 'N/A') {
            try {
              // Check cache first
              const cachedName = convoyDetailsCache.getVehicle(vehicleId);
              if (cachedName) {
                vehicleName = cachedName;
              } else {
                const vehicleResponse = await getVehicleById(vehicleId);
                vehicleName = vehicleResponse?.model_name || vehicleResponse?.vehicle_name || vehicleId;
                convoyDetailsCache.setVehicle(vehicleId, vehicleName);
              }
            } catch (error) {
              console.error(`Error fetching vehicle details for ${vehicleId}:`, error);
              vehicleName = vehicleId; // Fallback to vehicle ID
            }
          }

          // Use driver name from convoyMember data
          if (userId && userId !== 'N/A') {
            driverName = convoyMember.driverName || "Unknown Driver";
          }

          // Fetch parking destination (docking station) - check for docking_station field first, then use toSiteId
          const dockingStationId = convoyMember.docking_station || toSiteId;
          if (dockingStationId) {
            try {
              // Check cache first
              const cachedName = convoyDetailsCache.getSite(dockingStationId);
              if (cachedName) {
                parkingDestination = cachedName;
              } else {
                const siteResponse = await getSiteDetails(dockingStationId);
                parkingDestination = siteResponse?.site_name || siteResponse?.name || "Unknown Station";
                convoyDetailsCache.setSite(dockingStationId, parkingDestination);
              }
            } catch (error) {
              console.error(`Error fetching site details for ${dockingStationId}:`, error);
              parkingDestination = convoyMember.endSiteName || "Unknown Station";
            }
          }

          // Fetch speed and battery from driver stats
          if (rideId && rideId !== 'N/A' && userId && userId !== 'N/A') {
            try {
              // Check cache first
              const cachedStats = dashboardCache.getDriverStats(rideId, userId);
              if (cachedStats) {
                speed = cachedStats?.average_speed_kmh_last_minute?.toString() || "N/A";
                battery = cachedStats?.latest_battery?.toString() || "N/A";
              } else {
                const stats = await driverApi.getDriverStats(rideId, userId);
                dashboardCache.setDriverStats(rideId, userId, stats);
                speed = stats?.average_speed_kmh_last_minute?.toString() || "N/A";
                battery = stats?.latest_battery?.toString() || "N/A";
              }
            } catch (error) {
              console.error(`Error fetching driver stats for rideId ${rideId}, userId ${userId}:`, error);
              // Keep default values
            }
          }

          // Fetch driver status to determine ride status message
          if (rideId && rideId !== 'N/A' && userId && userId !== 'N/A') {
            try {
              // Check cache first
              let driverStatus = dashboardCache.getDriverStatus(rideId, userId);
              if (!driverStatus) {
                driverStatus = await driverApi.getDriverStatus(rideId, userId);
                dashboardCache.setDriverStatus(rideId, userId, driverStatus);
              }
              const status = driverStatus?.status?.toString() || "Unknown";
              const attendanceDetails = driverStatus?.attendance_details;

              if (attendanceDetails) {
                const onDuty = attendanceDetails.on_duty || false;
                const startAttendance = attendanceDetails.start_attendance || false;
                const endAttendance = attendanceDetails.end_attendance || false;

                // Determine status message based on status and attendance details
                if (status === "Duty not started") {
                  statusMessage = "Ride is not started.";
                } else if (status === "On duty" && onDuty) {
                  if (startAttendance && endAttendance) {
                    statusMessage = "Ride is successfully done!";
                  } else if (startAttendance && !endAttendance) {
                    statusMessage = "Ride in progress.";
                  } else {
                    statusMessage = "Ride is not started.";
                  }
                } else if (status === "Duty ended") {
                  statusMessage = "Ride is successfully done!";
                } else {
                  statusMessage = "Ride is not started.";
                }
              } else {
                // Fallback to status-based logic if attendance_details is not available
                if (status === "Duty not started") {
                  statusMessage = "Ride is not started.";
                } else if (status === "On duty") {
                  statusMessage = "Ride in progress.";
                } else if (status === "Duty ended") {
                  statusMessage = "Ride is successfully done!";
                } else {
                  statusMessage = "Ride is not started.";
                }
              }
            } catch (error) {
              console.error(`Error fetching driver status for rideId ${rideId}, userId ${userId}:`, error);
              statusMessage = "Status unavailable";
            }
          }

          // Return enriched data for table display
          return {
            serialNumber: serialNumber,
            vehicleId: vehicleId,
            vehicleName: vehicleName,
            userId: userId,
            driverName: driverName,
            parkingDestination: parkingDestination,
            material: material,
            speed: speed,
            battery: battery,
            statusMessage: statusMessage,
            endSiteName: parkingDestination, // For backward compatibility
            status: statusMessage, // For backward compatibility
          };
        })
      );

      setRideTableData(enrichedData);
      setLoadingTableData(false);
    } catch (error) {
      console.error("Error fetching ride info table data:", error);
      // Fallback to basic data if fetching fails
      const dataToShow = Array.isArray(rideList) && rideList.length > 0 
        ? rideList 
        : liveRides;
      setRideTableData(Array.isArray(dataToShow) ? dataToShow : []);
      setLoadingTableData(false);
    }
  }, [liveRides]);

  // Callback for closing ride info table
  const handleCloseRideInfoTable = useCallback(() => {
    setShowRideInfoTable(false);
    setRideTableData([]);
  }, []);

  // Handler for dispatching new rides
  const handleDispatchNewRide = ride => {
    console.log('Dispatching new ride:', ride);
    // Implement actual dispatch logic here
  };

  // Compute filtered office locations based on live rides
  // First, filter to show only sites that are present in Track Live Rides
  // Then, if a ride is selected, further filter to show only that ride's From and To sites
  const filteredOfficeLocations = useMemo(() => {
    // If no live rides, return empty array (no sites to show)
    if (!liveRides.length || !allOfficeLocations.length) {
      return [];
    }
    
    // Extract all unique site IDs from live rides (from both from_site_id and to_site_id)
    const siteIdsInLiveRides = new Set();
    liveRides.forEach(ride => {
      if (ride.from_site_id) {
        siteIdsInLiveRides.add(ride.from_site_id);
      }
      if (ride.to_site_id) {
        siteIdsInLiveRides.add(ride.to_site_id);
      }
    });
    
    // Filter to show only sites that are in any of the live rides
    let filtered = allOfficeLocations.filter(site => 
      siteIdsInLiveRides.has(site.id)
    );
    
    // If a ride is selected, further filter to show only that ride's From and To sites
    if (selectedRideForMap) {
      const selectedRide = liveRides.find(ride => ride.id === selectedRideForMap);
      if (selectedRide) {
        const fromSiteId = selectedRide.from_site_id;
        const toSiteId = selectedRide.to_site_id;
        
        filtered = filtered.filter(site => 
          site.id === fromSiteId || site.id === toSiteId
        );
      }
    }
    
    return filtered;
  }, [selectedRideForMap, liveRides, allOfficeLocations]);

  // Update officeLocations when filtered list changes
  useEffect(() => {
    setOfficeLocations(filteredOfficeLocations);
  }, [filteredOfficeLocations]);

  // Mobile viewport detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Mobile breakpoint: < 768px
      const isMobile = width < 768;
      setIsMobileViewport(isMobile);
      setViewportWidth(width);
    };

    // Check on mount
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Adjust map to show only the selected ride's From and To sites and calculate route
  useEffect(() => {
    // Clear route if no ride is selected
    if (!selectedRideForMap) {
      if (mapComponentRef.current && mapComponentRef.current.clearRoute) {
        mapComponentRef.current.clearRoute();
      }
      // Clear selected driver when going back to main view
      if (selectedDriver) {
        setSelectedDriver(null);
      }
      // Clear all driver tracking markers when no ride is selected
      if (mapComponentRef.current && mapComponentRef.current.clearDriverTracking) {
        mapComponentRef.current.clearDriverTracking();
      }
      if (mapComponentRef.current && mapComponentRef.current.clearConvoyDriverMarkers) {
        mapComponentRef.current.clearConvoyDriverMarkers();
      }
      return;
    }

    if (!mapComponentRef.current || filteredOfficeLocations.length < 1) {
      return;
    }

    // Find the selected ride to get From and To site IDs
    const selectedRide = liveRides.find(ride => ride.id === selectedRideForMap);
    if (!selectedRide) {
      return;
    }

    // Wait for map to be fully initialized
    const timer = setTimeout(async () => {
      if (!mapComponentRef.current) return;

      // Find From and To sites from filtered locations
      const fromSite = filteredOfficeLocations.find(site => site.id === selectedRide.from_site_id);
      const toSite = filteredOfficeLocations.find(site => site.id === selectedRide.to_site_id);

      if (fromSite && toSite && mapComponentRef.current.calculateRoute) {
        try {
          // Extract halt locations (waypoints) from ride halts
          const waypoints = [];
          if (selectedRide.halts && Array.isArray(selectedRide.halts)) {
            selectedRide.halts.forEach((halt, index) => {
              if (halt.coordinates && halt.coordinates.latitude && halt.coordinates.longitude) {
                waypoints.push({
                  lat: halt.coordinates.latitude,
                  lng: halt.coordinates.longitude,
                  address: halt.address || `Halt ${index + 1}`,
                });
              }
            });
          }

          console.log(`🛑 Debug: Found ${waypoints.length} halt waypoints for route`);

          // Add halt markers to the map
          if (mapComponentRef.current.addHaltMarkers && selectedRide.halts) {
            mapComponentRef.current.addHaltMarkers(selectedRide.halts);
          }

          // Calculate and display route using Google Maps Directions Service
          // The Directions Renderer will automatically adjust bounds to show the route
          await mapComponentRef.current.calculateRoute(
            { lat: fromSite.lat, lng: fromSite.lng },
            { lat: toSite.lat, lng: toSite.lng },
            waypoints // Pass waypoints to include halts in the route
          );

          // After route is displayed, adjust map to position on left side
          // Wait a bit for the route to render first
          setTimeout(() => {
            if (mapComponentRef.current && mapComponentRef.current.fitBounds && filteredOfficeLocations.length >= 2) {
              // Include halt locations in bounds calculation
              const bounds = filteredOfficeLocations.map(site => [site.lat, site.lng]);
              
              // Add halt locations to bounds
              if (waypoints.length > 0) {
                waypoints.forEach(wp => {
                  bounds.push([wp.lat, wp.lng]);
                });
              }
              
              // Calculate differences to determine padding
              const latDiff = Math.max(...bounds.map(b => b[0])) - 
                              Math.min(...bounds.map(b => b[0]));
              const lngDiff = Math.max(...bounds.map(b => b[1])) - 
                              Math.min(...bounds.map(b => b[1]));
              
              // Extend bounds to the right (east) to push sites to the left side of viewport
              // Using 2.5x the longitude difference to ensure sites appear on the left
              const eastBound = Math.max(...bounds.map(b => b[1]));
              const extendedEast = eastBound + Math.max(lngDiff * 2.5, 0.01);
              
              // Add extended point to shift viewport (using average lat for the extended point)
              const avgLat = bounds.reduce((sum, b) => sum + b[0], 0) / bounds.length;
              bounds.push([avgLat, extendedEast]);
              
              mapComponentRef.current.fitBounds(bounds);
            }
          }, 300); // Wait for route to render
        } catch (error) {
          console.error('Error calculating route:', error);
          // Fallback: just adjust map bounds without route
          if (mapComponentRef.current.fitBounds && filteredOfficeLocations.length >= 2) {
            const bounds = filteredOfficeLocations.map(site => [site.lat, site.lng]);
            const lngDiff = Math.max(...filteredOfficeLocations.map(s => s.lng)) - 
                            Math.min(...filteredOfficeLocations.map(s => s.lng));
            const eastBound = Math.max(...filteredOfficeLocations.map(s => s.lng));
            const extendedEast = eastBound + Math.max(lngDiff * 2.5, 0.01);
            const avgLat = filteredOfficeLocations.reduce((sum, s) => sum + s.lat, 0) / filteredOfficeLocations.length;
            bounds.push([avgLat, extendedEast]);
            mapComponentRef.current.fitBounds(bounds);
          }
        }
      } else if (filteredOfficeLocations.length === 1 && mapComponentRef.current.panToAndZoom) {
        // If only one site, center on it with high zoom
        try {
          mapComponentRef.current.panToAndZoom(
            { lat: filteredOfficeLocations[0].lat, lng: filteredOfficeLocations[0].lng },
            14
          );
        } catch (error) {
          console.error('Error panning to site:', error);
        }
      }
    }, 800); // Wait for map to be fully ready
    
    return () => clearTimeout(timer);
  }, [selectedRideForMap, filteredOfficeLocations, liveRides]);


  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Authentication Required
          </h2>
          <p className="text-gray-700 mb-4">
            You need to be logged in to access the Live Rides dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-700">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading Live Rides Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-600">
        <div className="text-center">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Main Content Area - Map and Sidebar Layout */}
      {/* Mobile: flex-col (vertical), Desktop: flex-row (horizontal) */}
      <div className={`flex flex-1 w-full overflow-hidden gap-4 ${isMobileViewport ? 'flex-col' : 'flex-row'}`}>
        {/* Map Section */}
        {/* Mobile: Full width with height = viewport width, Desktop: Flex-1 on left */}
        <div className={`${isMobileViewport ? 'w-full' : 'flex-1'} flex flex-col ${isMobileViewport ? '' : 'h-full'}`}>
          {/* Map Container */}
          <div 
            className="dashboard-card map-card relative w-full rounded-xl overflow-hidden shadow-xl flex-shrink-0"
            style={isMobileViewport ? {
              height: `${viewportWidth}px`, // Height equals viewport width on mobile
              width: '100%'
            } : {
              height: '100%',
              width: '100%'
            }}
          >
            {/* Map Component */}
            <MapComponent
              ref={mapComponentRef}
              officeLocations={officeLocations}
              guardLocations={employees.filter(emp => 
                emp.role === 'Guard' && 
                emp.lat != null && 
                emp.lng != null
              )}
              onMarkerClick={() => {}} // No site selection in Live Rides view
              mapCenter={mapCenter}
              mapZoom={mapZoom}
              activeRoutes={null} // Route is now handled by Directions Service
              showDrawingTools={false} // Disable drawing tools for Live Rides dashboard
            />
          </div>
        </div>

        {/* Live Rides Sidebar */}
        {/* Mobile: Full width below map, Desktop: Fixed width on right */}
        <div 
          className={`${isMobileViewport ? 'w-full flex-1' : 'w-96 flex-shrink-0'} overflow-hidden bg-white/10 backdrop-blur-md border border-white/10 rounded-xl shadow-lg`}
          style={isMobileViewport ? {
            height: 'auto',
            minHeight: '300px'
          } : {
            height: '100%'
          }}
        >
          <TrackRidesComponent
            liveRides={liveRides}
            officeLocations={officeLocations}
            employees={employees}
            onDispatchNewRide={handleDispatchNewRide}
            onClose={() => {}} // No close functionality in dedicated view
            onSelectRideForMap={setSelectedRideForMap}
            selectedRideIdForMap={selectedRideForMap}
            onShowEmployeeInfo={handleShowRideInfoTable}
            hideCloseButton={true} // Hide close button in dedicated dashboard view
            onRefresh={() => fetchLiveRides(true)} // Pass refresh function with force refresh
            onDriverClick={setSelectedDriver} // Handler for driver click to show traveled path
            selectedDriver={selectedDriver} // Currently selected driver
          />
        </div>
      </div>

      {/* Ride Info Table Modal */}
      <AnimatePresence>
        {showRideInfoTable && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-full h-full bg-white rounded-xl shadow-lg p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <Typography variant="h5" className="text-black font-bold font-['Figtree']">
                  Ride Information
                </Typography>
                <button
                  onClick={handleCloseRideInfoTable}
                  className="w-8 h-8 bg-neutral-50/50 rounded-full shadow-lg flex items-center justify-center"
                >
                  <XCircle className="w-6 h-6 text-black" />
                </button>
              </div>
              
              {/* Ride Table Content */}
              <div className="w-full overflow-x-auto overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(100% - 100px)' }}>
                {loadingTableData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="w-8 h-8 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading ride information...</p>
                    </div>
                  </div>
                ) : (
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 bg-neutral-50/10 rounded-tl-lg text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">S.No</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Vehicle</th>
                      <th className="px-4 py-3 bg-neutral-50/10 text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Driver</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Parking destination</th>
                      <th className="px-4 py-3 bg-neutral-50/10 text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Material</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Speed(km/h)</th>
                      <th className="px-4 py-3 bg-neutral-50/10 text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal border-r border-neutral-300">Battery(%)</th>
                      <th className="px-4 py-3 bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,_rgba(10,_128,_137,_0.09)_0%,_rgba(255,_255,_255,_0.72)_100%)] rounded-tr-lg text-center text-black text-lg font-semibold font-['Open_Sans'] leading-normal">Ride Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(rideTableData) && rideTableData.length > 0 ? (
                      rideTableData.map((row, index) => {
                        const isEven = index % 2 === 0;
                        const rowBgClass = isEven ? 'bg-white' : 'bg-blue-50';
                        
                        // Check if this is convoy data (driver info) or ride data
                        const isConvoyData = row.serialNumber !== undefined || row.vehicleId !== undefined;
                        
                        if (isConvoyData) {
                          // Display convoy/driver data with enriched information
                          // Determine status display and class based on statusMessage
                          let statusDisplay = 'Active';
                          let statusClass = 'bg-green-100 text-green-800';
                          
                          if (row.statusMessage) {
                            if (row.statusMessage.includes('successfully done')) {
                              statusDisplay = 'Completed';
                              statusClass = 'bg-blue-100 text-blue-800';
                            } else if (row.statusMessage.includes('in progress')) {
                              statusDisplay = 'Active';
                              statusClass = 'bg-green-100 text-green-800';
                            } else if (row.statusMessage.includes('not started')) {
                              statusDisplay = 'Not Started';
                              statusClass = 'bg-gray-100 text-gray-800';
                            } else {
                              statusDisplay = row.statusMessage;
                              statusClass = 'bg-gray-100 text-gray-800';
                            }
                          } else if (row.status) {
                            statusDisplay = row.status === 'in progress' ? 'Active' : row.status === 'completed' ? 'Completed' : row.status === 'not started' ? 'Not Started' : row.status || 'Active';
                            statusClass = statusDisplay === 'Active' ? 'bg-green-100 text-green-800' : statusDisplay === 'Completed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800';
                          }
                          
                          return (
                            <tr key={row.userId || row.vehicleId || index} className={rowBgClass}>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.serialNumber || index + 1}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.vehicleName || row.vehicleId || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.driverName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.parkingDestination || row.endSiteName || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.material || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.speed && row.speed !== 'N/A' ? `${row.speed} km/h` : 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.battery !== null && row.battery !== undefined && row.battery !== 'N/A' ? `${row.battery}%` : 'N/A'}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={combineClasses("px-3 py-1 rounded-full text-xs font-semibold", statusClass)}>
                                  {statusDisplay}
                                </span>
                              </td>
                            </tr>
                          );
                        } else {
                          // Display ride data (original format)
                          const statusDisplay = row.status === 'in progress' ? 'Active' : row.status === 'completed' ? 'Completed' : row.status === 'not started' ? 'Not Started' : row.status || 'Active';
                          const statusClass = statusDisplay === 'Active' ? 'bg-green-100 text-green-800' : statusDisplay === 'Completed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800';
                          return (
                            <tr key={row.id || index} className={rowBgClass}>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{index + 1}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.vehicle || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.driver || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.endSiteName || row.destination || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.material || 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.speed ? `${row.speed}kmph` : 'N/A'}</td>
                              <td className="px-4 py-3 text-center text-black text-base font-normal font-['Open_Sans'] leading-relaxed border-r border-neutral-300">{row.battery !== null && row.battery !== undefined ? `${row.battery}` : 'N/A'}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={combineClasses("px-3 py-1 rounded-full text-xs font-semibold", statusClass)}>
                                  {statusDisplay}
                                </span>
                              </td>
                            </tr>
                          );
                        }
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="py-4 text-center text-gray-600 bg-white/80">No ride data available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveRidesDashboard;
